/**
 * ErrorBoundary - React error boundary for graceful degradation
 *
 * Catches JavaScript errors in child component tree and displays fallback UI.
 * Errors are captured to both Sentry (centralized error tracking) and PostHog
 * (user behavior correlation).
 *
 * Used to wrap animation components that may fail due to:
 * - WebGL/Canvas issues on older browsers
 * - Memory pressure on low-end devices
 * - Race conditions during rapid state changes
 *
 * Usage:
 * <ErrorBoundary fallback={<StaticFallback />}>
 *   <AnimationComponent />
 * </ErrorBoundary>
 *
 * @see OBSERVABILITY_HUB.md for Sentry integration details
 */
import React, { Component, ErrorInfo, ReactNode } from "react";
import { View, Text, Pressable } from "react-native";
import { i18n } from "@dramadetector/shared";
import { captureEvent, captureError } from "../lib/analytics";
import { scrubString } from "../lib/analytics/scrubbers";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback UI to render when error occurs */
  fallback?: ReactNode;
  /** Optional callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Component name for analytics tracking */
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Capture error to Sentry for centralized error tracking
    captureError(error, {
      component: this.props.componentName || "unknown",
      componentStack: errorInfo.componentStack?.slice(0, 1000), // Truncate for payload size
      origin: "ErrorBoundary",
    });

    // Track error for PostHog analytics (user behavior correlation)
    // M4.2: Scrub error_message to prevent PII leakage to PostHog
    captureEvent("error_boundary_triggered", {
      component: this.props.componentName || "unknown",
      error_message: scrubString(error.message),
      error_name: error.name,
      component_stack: errorInfo.componentStack?.slice(0, 500), // Truncate for payload size
    });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);

    // Log to console in development
    if (__DEV__) {
      console.error("[ErrorBoundary] Caught error:", error);
      console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Return custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <View className="flex-1 items-center justify-center p-6">
          <Text className="text-lg font-semibold text-gray-800 mb-2">
            {i18n.t("error_boundary.title", { ns: "common", defaultValue: "Something went wrong" })}
          </Text>
          <Text className="text-sm text-gray-600 text-center mb-4">
            {i18n.t("error_boundary.description", { ns: "common", defaultValue: "We encountered an issue displaying this content." })}
          </Text>
          <Pressable
            onPress={this.handleRetry}
            className="bg-purple-500 px-6 py-3 rounded-xl active:opacity-80"
          >
            <Text className="text-white font-medium">
              {i18n.t("try_again", { ns: "common", defaultValue: "Try Again" })}
            </Text>
          </Pressable>
        </View>
      );
    }

    return this.props.children;
  }
}

/**
 * AnimationErrorBoundary - Specialized error boundary for animations
 *
 * Provides animation-specific fallback that shows static content
 * instead of crashing the entire screen.
 */
interface AnimationErrorBoundaryProps {
  children: ReactNode;
  /** Static fallback content to show on animation failure */
  staticFallback: ReactNode;
}

export function AnimationErrorBoundary({
  children,
  staticFallback,
}: AnimationErrorBoundaryProps): React.JSX.Element {
  return (
    <ErrorBoundary
      fallback={staticFallback}
      componentName="LetterThrowingAnimation"
      onError={(error) => {
        // Additional animation-specific error handling
        // M4.2: Scrub error_message to prevent PII leakage to PostHog
        captureEvent("animation_error", {
          animation_type: "letter_throwing",
          error_message: scrubString(error.message),
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
